import React, {startTransition, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useLocation, useHistory} from 'react-router-dom'
import {Box, Switch, Text, Link, HStack, VStack} from '@chakra-ui/react'
import {FormattedMessage, useIntl} from 'react-intl'
import {useStoreContext} from '../../../contexts/store'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useTagManager from '../../../hooks/use-tag-manager'
import {scheduleAfterPaint, scheduleIdleAnalytics} from '../../../utils/common-utils'

const StoreRefinement = ({selectedFilters, inStockAt}) => {
    const location = useLocation()
    const history = useHistory()
    const intl = useIntl()
    const {store, onOpen} = useStoreContext()
    const blockStoreSelection =
        !!store && !inStockAt?.find((s) => s?.name?.indexOf(store?.ID) !== -1)
    const tagManager = useTagManager()
    const {
        app: {commerceAPI}
    } = getConfig()
    const {config} = commerceAPI.bloomreach || {}
    var enableBloomreachSearch = null
    try {
        if (config) {
            enableBloomreachSearch = JSON.parse(config).enableBloomreachSearch
        }
    } catch (e) {
        console.error(e)
    }
    const refinementField = enableBloomreachSearch ? 'in_stock_at=' : 'c_availableInStores='
    const getUpdatedSearch = (storeId) => {
        const params = new URLSearchParams(location.search)

        const refinements = params
            .getAll('refine')
            .filter((r) => !r.startsWith(`${refinementField}`))
        params.delete('refine')

        refinements.forEach((r) => params.append('refine', r))

        if (storeId) {
            params.append('refine', `${refinementField}${storeId}`)
        }

        return params.toString()
    }

    const urlSelected = new URLSearchParams(location.search)
        .getAll('refine')
        .some((r) => store && r === `${refinementField}${store.id || store.ID}`)
    const [optimisticSelected, setOptimisticSelected] = useState(null)
    const isSelected = optimisticSelected ?? urlSelected

    useEffect(() => {
        setOptimisticSelected(null)
    }, [location.search])

    const handleToggle = (e) => {
        if (!store) {
            onOpen()
            return
        }
        const checked = e.target.checked
        setOptimisticSelected(checked)
        const newSearch = getUpdatedSearch(checked ? store.id || store.ID : null)
        scheduleAfterPaint(() => {
            startTransition(() => {
                history.push(`${location.pathname}?${newSearch}`)
            })
        })

        const nextSelectedFilters = {...selectedFilters}
        if (checked) {
            if (enableBloomreachSearch) {
                nextSelectedFilters.in_stock_at = [
                    store?.name?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                ]
            } else {
                nextSelectedFilters.c_availableInStores = [
                    store?.name?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                ]
            }
        } else {
            if (enableBloomreachSearch) {
                delete nextSelectedFilters.in_stock_at
            } else {
                delete nextSelectedFilters.c_availableInStores
            }
        }
        if (!checked) {
            delete nextSelectedFilters.c_availableInStores
            delete nextSelectedFilters.in_stock_at
        }
        scheduleIdleAnalytics(() => {
            var filterPayload = Object.entries(nextSelectedFilters).reduce(
                (acc, [label, values]) => {
                    acc[
                        `filter${label
                            ?.toLowerCase()
                            .replace(/\b\w/g, (char) => char.toUpperCase())}`
                    ] = Array.isArray(values) ? values.join(', ') : values
                    return acc
                },
                {}
            )
            var filterOptionSelected = nextSelectedFilters
                ? Object.values(nextSelectedFilters).flat().join(', ')
                : ''
            filterOptionSelected = filterOptionSelected.replace(
                store?.ID,
                store?.name.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
            )
            delete filterPayload.filterIn_stock_at
            delete filterPayload.filterC_availableinstores
            tagManager.productsFilter({
                event: 'filter',
                filterMethod: 'toggle',
                filterSort: document
                    .querySelector('.product-list-sort select')
                    ?.selectedOptions?.[0]?.innerText?.trim(),
                filterOptionSelected: filterOptionSelected,
                filterSelected: intl.formatMessage({
                    id: 'instock.near.me',
                    defaultMessage: 'In Stock Near Me'
                }),
                filterStoreStock: checked ? 'true' : 'false',
                ...filterPayload
            })
        })
    }

    const handleSelectStoreClick = () => {
        onOpen()
    }

    return (
        <Box
            marginBottom={{base: '20px', lg: '30px'}}
            marginTop={{base: '0px', lg: '-10px'}}
            className="store-refinement-wrapper"
        >
            <HStack gap="30px" display="inline-flex" alignItems="normal">
                <VStack spacing={0} align="start">
                    <Text
                        fontWeight="400"
                        fontSize="16px"
                        letterSpacing="0.08px"
                        lineHeight="25px"
                        color="black.100"
                    >
                        <FormattedMessage id="instock.near.me" defaultMessage="In Stock Near Me" />
                    </Text>
                    <Link
                        textDecor="underline"
                        color={blockStoreSelection ? 'red.400' : 'teal.500'}
                        fontWeight="300"
                        fontSize="14px"
                        lineHeight="22px"
                        letterSpacing="0.07px"
                        textUnderlineOffset="4px"
                        onClick={handleSelectStoreClick}
                        cursor="pointer"
                    >
                        {store ? (
                            store.name?.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase())
                        ) : (
                            <FormattedMessage id="select.a.store" defaultMessage="Select a Store" />
                        )}
                    </Link>
                    {blockStoreSelection && (
                        <Text
                            marginTop="10px"
                            color="red.400"
                            fontSize="14px"
                            maxWidth="200px"
                            fontWeight="300"
                            letterSpacing="0.07px"
                        >
                            <FormattedMessage
                                id="no.matching.products.found"
                                values={{
                                    name: store.name
                                        ?.toLowerCase()
                                        .replace(/\b\w/g, (char) => char.toUpperCase())
                                }}
                                defaultMessage="No matching products found at {name}"
                            />
                        </Text>
                    )}
                </VStack>
                <Switch
                    isChecked={isSelected}
                    data-selected={isSelected}
                    paddingTop="3px"
                    isDisabled={blockStoreSelection}
                    width="27px"
                    height="17px"
                    onChange={handleToggle}
                    sx={{
                        '.chakra-switch__track': {
                            bg: 'gray.200',
                            border: '1px solid',
                            borderColor: 'gray.600',
                            height: '15px',
                            width: '25px',
                            padding: '0',
                            borderRadius: '10px'
                        },
                        '.chakra-switch__thumb': {
                            width: '12px',
                            height: '12px',
                            bg: isSelected ? 'purple.100' : 'gray.600',
                            transform: isSelected
                                ? 'translateX(11px) translateY(1.5px) !important'
                                : 'translateX(2px) translateY(1.5px) !important',
                            transition: 'all 0.3s ease'
                        }
                    }}
                />
            </HStack>
        </Box>
    )
}

StoreRefinement.propTypes = {
    selectedFilters: PropTypes.any,
    inStockAt: PropTypes.any
}

export default StoreRefinement
